import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { BuilderContent, fetchEntries, GetContentOptions } from '@builder.io/sdk-angular';
import { BuilderConfigService } from '@scalefast/config-angular';
import { BuilderContentError } from '@scalefast/core';
import {
  BuilderPageData,
  BuilderPageResultData,
  GenericLogger,
  IS_BUILDER_EDITOR,
  MECACHIS_DEPENDENCY_SERVICE,
} from '@scalefast/ecommerce-core';
import { catchError, firstValueFrom, from, map, Observable, of } from 'rxjs';

/**
 * FIXME: This interface will be removed when we export the ContentIdUpdateTimestamp type from Mecachis.
 * https://setnext.scalefast.ninja/scalefast/frontend/pandora/-/merge_requests/1332
 */
interface ContentIdUpdateTimestamp {
  id: string;
  lastUpdated: number;
}

@Injectable({
  providedIn: 'root',
})
/**
 * BuilderGen2Service is a service that provides methods to interact with the Builder.io API using the SDK Gen 2.
 * It allows fetching content by model name, getting page content by content options, and prefetching popular pages.
 */
export class BuilderGen2Service {
  #builderIOApiKey: string;
  #isBuilderEditor = inject(IS_BUILDER_EDITOR, { optional: true });
  #builderConfigService = inject(BuilderConfigService);
  #logger = inject(GenericLogger);
  #mecachisDependencyService = inject(MECACHIS_DEPENDENCY_SERVICE);
  #httpClient = inject(HttpClient);

  constructor() {
    this.#builderIOApiKey = this.#builderConfigService.get().builderIOApiKey;
  }

  /**
   * It gets the content from builder API by the modelName. It's used from different elements in the application.
   * @param modelName The modelName to get the content from Builder.io
   * @returns Observable of BuilderContent
   */
  getContentByModelName(modelName: string): Observable<BuilderContent> {
    return this.getContentsByModelName(modelName).pipe(
      map((content) => {
        if (!content[0]) {
          this.#mecachisDependencyService.addErrorDependency(`getContentByModelName: No content found for model ${modelName}`);
          throw new BuilderContentError(`getContentByModelName: No content found for model ${modelName}`);
        }
        if (content.length > 1) {
          this.#logger.warn({
            msg: `getContentByModelName. Unexpected more than one result found for model ${modelName}`,
            modelName,
          });
        }
        return content[0];
      }),
    );
  }

  /**
   * It gets the array of contents from builder API by the modelName using SDK Gen 2.
   * This method doesn't have pagination. It should be implemented in specific pagination method.
   * @param modelName The modelName to get the content from Builder.io
   * @param options Some elements send their own options to the API to filter the content.
   * @returns Observable of BuilderContent[]
   */
  getContentsByModelName(modelName: string, options?: Partial<GetContentOptions>): Observable<BuilderContent[]> {
    const contentOptions: Partial<GetContentOptions> = {
      limit: 100,
      ...options,
    };
    const builderPromise = this.#fetchEntriesByModelName(modelName, contentOptions);
    return from(builderPromise);
  }

  /**
   * @deprecated compatibility with gen 1 service Pandora use. Use getPageByContentOptions
   */
  getPageByParams(options: Partial<GetContentOptions>): Observable<BuilderPageData | undefined> {
    return this.getPageByContentOptions(options);
  }

  /**
   * It gets the page content from Builder.io by the builderParams received using SDK Gen 2.
   * @param options Builder options created from the urlPath and queryParams
   * @returns Observable of BuilderPageData | undefined
   */
  getPageByContentOptions(options: Partial<GetContentOptions>): Observable<BuilderPageData | undefined> {
    const urlPath = options.userAttributes?.urlPath;
    const contentOptions: Partial<GetContentOptions> = {
      limit: 1,
      userAttributes: { urlPath },
      ...options,
    };
    const builderPromise = this.#fetchEntriesByModelName('page', contentOptions);
    return from(builderPromise).pipe(
      map((builderContent) => {
        if (!builderContent || !builderContent.length) {
          this.#logger.warn({
            msg: `getPageByContentOptions. No Builder content found for page ${urlPath}`,
            contentOptions,
          });
          return;
        }
        if (builderContent.length > 1) {
          this.#logger.warn({
            msg: `getPageByContentOptions. Unexpected more than one result found for page ${urlPath}`,
            contentOptions,
          });
        }
        return builderContent[0].data as BuilderPageData;
      }),
    );
  }

  /**
   * Fetches a single entry from Builder.io for rendering purposes (enrich: true).
   * TODO: In some cases we may be able to fetch earlier on the resolver or multiple content at the same time, fetchForRender
   * is the cleanest transition from the current system, but we should review for improvements
   * https://setnext.scalefast.ninja/groups/scalefast/frontend/-/epics/63
   * @param model The model to fetch the content from.
   * @param urlPath The URL path to use as user attributes for builder.io targeting system
   * @returns A promise that resolves to the fetched BuilderContent or null if not found.
   */
  fetchForRender(model: string, urlPath?: string): Observable<BuilderContent | null> {
    const contentOptions: Partial<GetContentOptions> = {
      limit: 1,
      enrich: true,
      userAttributes: { urlPath },
    };
    const builderPromise = this.#fetchEntriesByModelName(model, contentOptions);
    return from(builderPromise).pipe(
      map((builderContent) => {
        if (!builderContent || builderContent.length === 0) {
          this.#logger.warn({
            msg: `fetchForRender. No Builder content found for model ${model} and urlPath ${urlPath}`,
          });
          this.#mecachisDependencyService.addErrorDependency(
            `fetchForRender: No Builder content found for model ${model} and urlPath ${urlPath}`,
          );
          return null;
        }
        return builderContent[0];
      }),
      catchError((error) => {
        this.#logger.error({
          msg: `Error in fetchForRender for model ${model} and urlPath ${urlPath}`,
          error,
        });
        return of(null);
      }),
    );
  }

  /**
   * Get the Builder most popular pages set as prefetch in Builder.io using SDK Gen 2
   * @returns Promise of BuilderPageResultData[]
   */
  // TODO: when moving this service to Pandora we should check the types. We probably don't need the BuilderPageResultData type,
  // as the fetchEntries function returns BuilderContent directly. We need to check the filtered fields in the new SDK.
  async prefetchMostPopularPages(): Promise<BuilderPageResultData[]> {
    const contentOptions: Partial<GetContentOptions> = {
      query: {
        'data.prefetch': { $eq: true },
      },
      omit: 'meta.componentsUsed',
      enrich: true,
      fields: 'data.page,data.serverNavigation',
      fetch: (url: string, options: any) => this.#httpClientFetch(url, options, false),
    };
    try {
      const results = (await this.#fetchEntriesByModelName('page', contentOptions)) as BuilderPageResultData[];
      if (this.#isBuilderPageData(results)) {
        return Promise.all(results);
      }
      throw new BuilderContentError('BuilderGen2Service: PrefetchMostPopularPages response is not of the expected type');
    } catch (error) {
      throw new Error(`BuilderGen2Service: Error fetching most popular pages`, { cause: error });
    }
  }

  /**
   * It creates the Builder params needed for the page request.
   * The params are used to get the page content from Builder.io.
   * @param path url requested
   * @param queryParams params received from the url
   * @returns the builder params object
   */
  getPageRequestParams(path: string, queryParams?: Params): Params {
    const args: Record<any, any> = {
      omit: 'meta.componentsUsed',
      enrich: true,
      fields: 'data.page,data.serverNavigation',
      'userAttributes.urlPath': path,
      apiKey: this.#builderIOApiKey,
    };
    if (this.#isBuilderEditor) {
      // Include unpublished pages if current state is builder editor
      args['includeUnpublished'] = true;
    }
    if (this.#isBuilderEditor && queryParams?.builderid) {
      // Get page by id to show scheduled pages in builder editor
      delete args['userAttributes.urlPath'];
      args['query.id.$eq'] = queryParams.builderid;
    }
    return args;
  }

  /**
   * Private method that fetches entries from Builder.io by model name and parameters.
   * It also adds a dependency to Mecachis for revalidation.
   */
  async #fetchEntriesByModelName(modelName: string, contentOptions?: Partial<GetContentOptions>): Promise<BuilderContent[]> {
    let builderRequestUrl: string | undefined;
    try {
      const content = await fetchEntries({
        model: modelName,
        apiKey: this.#builderIOApiKey,
        options: contentOptions,
        fetch: (url, options) => {
          builderRequestUrl = url.toString();
          return this.#httpClientFetch(url, options);
        },
      });
      if (!builderRequestUrl) {
        throw new Error(`fetchEntriesByModelName: No builderRequestUrl was set for model ${modelName}`);
      }
      // Normalize the content to ensure it has the expected structure
      const pathToData = content[0]?.data?.page?.value ? 'data.page.value' : undefined;
      const normalizedBuilderContent: BuilderContent[] = content[0]?.data?.page?.value ? [content[0].data.page.value] : content;

      if (!this.#isBuilderContent(normalizedBuilderContent)) {
        this.#mecachisDependencyService.addErrorDependency(
          `fetchEntriesByModelName: Invalid content for model ${modelName} ${builderRequestUrl}`,
        );
        throw new BuilderContentError(`fetchEntriesByModelName: Invalid content for model ${modelName} ${builderRequestUrl}`);
      }
      this.#addMecachisRevalidableDependency(modelName, normalizedBuilderContent, builderRequestUrl, pathToData);
      return content;
    } catch (error) {
      this.#mecachisDependencyService.addErrorDependency(
        `fetchEntriesByModelName: Error fetching entries for model ${modelName}`,
      );
      throw new Error(`fetchEntriesByModelName: Error fetching entries for model ${modelName}`, { cause: error });
    }
  }

  /**
   * This is a wrapper for the HttpClient request to be used with Builder SDK Gen 2.
   * Builder docu here: https://www.builder.io/c/docs/content-api#code-fetch-code
   */
  // FIXME: type
  #httpClientFetch = async (url: string, options: any, useTransferCache = true) => {
    const response: any = await firstValueFrom(
      this.#httpClient.get<any>(url, {
        body: options.body,
        headers: options.headers,
        ...options,
        observe: 'response',
        responseType: 'json',
        transferCache: useTransferCache,
      }),
    );
    return {
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      json: () => Promise.resolve(response.body),
    };
  };

  /**
   * It checks if the content received from the API has the expected structure.
   * @param builderContent The content received from the Builder API.
   * @returns type predicate
   */
  #isBuilderContent(builderContent: any): builderContent is BuilderContent[] {
    return builderContent.every((content: any) => {
      if (!content) {
        return false;
      }
      return content.id && content.lastUpdated;
    });
  }

  /**
   * This function checks that the received object has a data object and a page property inside.
   * This helps us to identify if the request to Builder API was done with the specific 'page' field parameter, and so,
   * the Builder API response will include the page object.
   * TODO: when moving to pandora, move this function to the Builder.ts in Domain in ecommerce-core.
   */
  #isBuilderPageData(results: any): results is BuilderPageResultData[] {
    return results.every((element: any) => element.data && element.data.page !== undefined);
  }

  /**
   * Adds a revalidable dependency for the Mecachis service.
   * @param model The model name to be used in the request.
   * @param builderContent The content received from the Builder API.
   * @param builderRequestUrl The original request URL.
   * @param pathToData The path to the data within the response. This is needed in some cases to correctly identify
   * the content to revalidate.
   */
  #addMecachisRevalidableDependency(
    model: string,
    builderContent: BuilderContent[],
    builderRequestUrl: string,
    pathToData?: string,
  ): void {
    const url = `https://cdn.builder.io/api/v3/content/${model}`;
    const contentToRevalidate: ContentIdUpdateTimestamp[] = builderContent.map(({ id, lastUpdated }) => ({
      id: id!,
      lastUpdated: lastUpdated!,
    }));
    this.#logger.debug({
      msg: `addMecachisRevalidableDependency: addRevalidableBuilderDependency for page ${builderRequestUrl}`,
      builderContent,
    });
    // TODO: the revalidable dependencies will be refactored.
    // In the future we will change the querySearchParms to contentOptions to recreate the same fetchEntries request.
    const urlObject = new URL(builderRequestUrl);
    const queryParams = urlObject.searchParams;
    this.#mecachisDependencyService.addRevalidableBuilderDependency(builderRequestUrl, {
      contentToRevalidate,
      url,
      apiKey: this.#builderIOApiKey,
      queryType: 'content',
      querySearchParams: queryParams,
      pathToData,
      timeToRevalidate: 60 * 1,
    });
  }
}
